const PREFIX = 'pejvak_draft_';
const TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
const IDB_NAME = 'pejvak_drafts_db';
const IDB_STORE = 'file_drafts';
const IDB_VERSION = 1;

// localStorage is shared by the whole browser origin regardless of which
// account is logged in - without scoping by account, drafts written by one
// user show up for the next person on the same browser.
//
// Scoped by logged-in username (ACTIVE_USERNAME_KEY, synced by AuthContext
// BEFORE setUser). Not session_id - that is minted fresh on every login.
export const ACTIVE_USERNAME_KEY = 'pejvak_active_username';

const userScope = (username) => {
  const explicit = typeof username === 'string' ? username.trim() : '';
  if (explicit) return explicit;
  try {
    return (localStorage.getItem(ACTIVE_USERNAME_KEY) || '').trim() || 'anon';
  } catch { return 'anon'; }
};

const scopedKey = (key, username) => `${userScope(username)}_${key}`;

// When clearDraft runs but DraftTextarea unmounts before clearTrigger, useDraft's
// unmount flush would otherwise resurrect the text into localStorage.
const explicitlyCleared = new Set();

function markExplicitlyCleared(key, username) {
  explicitlyCleared.add(scopedKey(key, username));
}

function unmarkExplicitlyCleared(key, username) {
  explicitlyCleared.delete(scopedKey(key, username));
}

/** Used by useDraft unmount flush — skip re-save after an intentional clear. */
export function wasDraftExplicitlyCleared(key, username) {
  return explicitlyCleared.has(scopedKey(key, username));
}

/** Call before setUser(profile) so the next paint's DraftTextarea reads the right scope. */
export function syncDraftUsername(username) {
  try {
    const u = typeof username === 'string' ? username.trim() : '';
    if (u) localStorage.setItem(ACTIVE_USERNAME_KEY, u);
    else localStorage.removeItem(ACTIVE_USERNAME_KEY);
  } catch {}
}

export function saveDraft(key, value, username) {
  if (!key) return false;
  try {
    const sk = scopedKey(key, username);
    if (value == null || value === '') {
      localStorage.removeItem(PREFIX + sk);
      markExplicitlyCleared(key, username);
      return true;
    }
    unmarkExplicitlyCleared(key, username);
    localStorage.setItem(PREFIX + sk, JSON.stringify({ value, savedAt: Date.now() }));
    return true;
  } catch {
    return false;
  }
}

export function loadDraft(key, username) {
  if (!key) return null;
  try {
    const sk = scopedKey(key, username);
    let raw = localStorage.getItem(PREFIX + sk);

    // Migrate legacy unscoped keys (pejvak_draft_ticket_reply_21) and
    // drafts left under 'anon' while auth was still catching up.
    if (!raw) {
      const legacy = localStorage.getItem(PREFIX + key);
      if (legacy) {
        raw = legacy;
        localStorage.setItem(PREFIX + sk, legacy);
        localStorage.removeItem(PREFIX + key);
      }
    }
    if (!raw && userScope(username) !== 'anon') {
      const anonKey = PREFIX + `anon_${key}`;
      const orphan = localStorage.getItem(anonKey);
      if (orphan) {
        raw = orphan;
        localStorage.setItem(PREFIX + sk, orphan);
        localStorage.removeItem(anonKey);
      }
    }

    if (!raw) return null;
    const { value, savedAt } = JSON.parse(raw);
    if (Date.now() - savedAt > TTL) { clearDraft(key, username); return null; }
    return value;
  } catch { return null; }
}

export function clearDraft(key, username) {
  if (!key) return;
  try {
    localStorage.removeItem(PREFIX + scopedKey(key, username));
    // Also wipe legacy / anon leftovers for this key so they can't resurrect.
    localStorage.removeItem(PREFIX + key);
    localStorage.removeItem(PREFIX + `anon_${key}`);
    markExplicitlyCleared(key, username);
  } catch {}
}

/** Customer "new ticket" composer draft keys (text + optional IDB file staging). */
export const NEW_TICKET_TEXT_KEY = 'new_ticket_text';
export const NEW_TICKET_FILES_KEY = 'new_ticket_files';

/** Wipe all local new-ticket drafts after a successful send. Server-side
 *  tickets_draft / tickets_draft_files for main_ticket_id=0 are cleared by
 *  send_ticket; this handles localStorage + IndexedDB leftovers. */
export async function clearNewTicketDrafts(username) {
  clearDraft(NEW_TICKET_TEXT_KEY, username);
  await clearFileDraft(NEW_TICKET_FILES_KEY, username);
}

/** Follow-up composer on an existing ticket (text only today). */
export async function clearTicketFollowUpDrafts(ticketId, username) {
  if (ticketId == null || ticketId === '') return;
  clearDraft(`ticket_followup_${ticketId}`, username);
  clearDraft(`ticket_continuing_${ticketId}`, username);
  await clearFileDraft(`ticket_followup_files_${ticketId}`, username);
}

export function listDrafts(username) {
  const prefix = PREFIX + userScope(username) + '_';
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(prefix)) keys.push(k.slice(PREFIX.length));
  }
  return keys;
}

// ── File drafts (IndexedDB) ───────────────────────────────────────────
// Reply attachments are base64 data URLs — far too large for localStorage
// (QuotaExceeded kills the write). Keep them in IndexedDB so employee
// reply drafts survive refresh when a file is attached.
function openFileDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('idb open failed'));
  });
}

export async function saveFileDraft(key, files, username) {
  if (!key) return false;
  const id = PREFIX + scopedKey(key, username);
  try {
    const db = await openFileDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      if (!files || (Array.isArray(files) && files.length === 0)) {
        store.delete(id);
      } else {
        store.put({ value: files, savedAt: Date.now() }, id);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('idb write failed'));
    });
    db.close();
    return true;
  } catch {
    return false;
  }
}

export async function loadFileDraft(key, username) {
  if (!key) return null;
  const id = PREFIX + scopedKey(key, username);
  try {
    const db = await openFileDb();
    const row = await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error || new Error('idb read failed'));
    });
    db.close();
    if (!row) return null;
    if (Date.now() - (row.savedAt || 0) > TTL) {
      await clearFileDraft(key, username);
      return null;
    }
    return Array.isArray(row.value) ? row.value : null;
  } catch {
    return null;
  }
}

export async function clearFileDraft(key, username) {
  if (!key) return;
  const id = PREFIX + scopedKey(key, username);
  try {
    const db = await openFileDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('idb delete failed'));
    });
    db.close();
  } catch {}
}
